import {
  APPOINTMENT_AGENT_RTC_MODEL,
  APPOINTMENT_SECRET_KEY,
  CONVERSATION_ID_URL,
  SANP_AI_API_KEY,
  SYMPTOM_COLLECTOR_TRANSCRIPTION_MODEL,
} from "../../envConstants";
import { INPUT_AUDIO_NOISE_REDUCTION_TYPE } from "../symptomCollector/utils/constants";
import { generateUUID } from "../symptomCollector/symptomCollectorHelper";
import dayjs from "dayjs";

const appointmentApiSecret = APPOINTMENT_SECRET_KEY || "";
const appointmentApiUrl = `${CONVERSATION_ID_URL}/api/v1/receptionist-agent`;
export const createSession = async function (
  agentDetails: any,
  selectedLanguage: any,
  chat_history: any
) {
  const raw = JSON.stringify({
    language: selectedLanguage,
    voice: agentDetails?.agent_voice,
    hm_id: agentDetails?.hm_id,
    hm_business_id: agentDetails?.hm_business_id,
    hospital_name: agentDetails?.hospital_name,
    agent_gender: agentDetails?.agent_gender,
    agent_name: agentDetails?.agent_name,
    mobile_number: agentDetails?.patient_contact_no,
    chat_history,
  });
  const createWebRtcSessionUrl = `${appointmentApiUrl}/create-webrtc-session`;
  try {
    const method = "POST";
    const headers = {
      "x-api-key": appointmentApiSecret,
      "Content-Type": "application/json",
    };
    const response = await fetch(createWebRtcSessionUrl, {
      method,
      headers,
      body: raw,
    });

    const data = await response.json();
    const ephemeralKey = data?.result?.client_secret?.value ?? "";
    return ephemeralKey;
  } catch (error) {
    console.error("Error creating webrtc session:", error);
    return null;
  }
};

export async function establishWebRtcConnection(
  ephemeralKey: string,
  offer: RTCSessionDescriptionInit
) {
  const DEPLOYMENT = "sympton-collector-realtime";
  const WEBRTC_URL =
    "https://eastus2.realtimeapi-preview.ai.azure.com/v1/realtimertc";
  const azureUrl = `${WEBRTC_URL}?model=${DEPLOYMENT}`;

  try {
    const headers = {
      Authorization: `Bearer ${ephemeralKey}`,
      "Content-Type": "application/sdp",
    };
    const body = offer.sdp;
    const response = await fetch(azureUrl, {
      method: "POST",
      headers,
      body,
    });

    const data = await response.text();
    return data;
  } catch (error) {
    console.error("Error establishing webrtc session:", error);
    return null;
  }
}

const SESSIONS_URL =
  "https://sanp-ai.openai.azure.com/openai/realtimeapi/sessions?api-version=2025-04-01-preview";
const API_KEY = SANP_AI_API_KEY;
const DEPLOYMENT = "sympton-collector-realtime";

const MULTI_SPECIALITY_TOOL = [
  {
    type: "function",
    name: "get_specialities",
    description: "Get specialities for a given hospital ID",
    parameters: {
      type: "object",
      properties: {
        hm_id: {
          type: "integer",
          description: "Hospital Management ID",
        },
      },
      required: ["hm_id"],
    },
  },
];

const MULTI_DOCTOR_TOOL = [
  {
    type: "function",
    name: "get_doctors",
    description:
      "Fetch the list of doctors for a given hospital or speciality. Example: When user says 'I want to book with a cardiologist', call this tool using { hm_id: 12, filter_speciality: 'Cardiology' }.",
    parameters: {
      type: "object",
      properties: {
        hm_id: {
          type: "integer",
          description: "Hospital Management ID",
        },
        unique_speciality: {
          type: "boolean",
          description:
            "boolean true or false to return the unique specialities. Its an optional parameter",
        },
        filter_speciality: {
          type: "string",
          description:
            "speciality of which we want to filter out the doctors for. Its an optional parameter.",
        },
      },
      required: ["hm_id"],
    },
  },
];

const TOOLS = [
  {
    type: "function",
    name: "get_appointment_slots",
    description:
      "Get available appointment slots for a given doctor and date. Example: After user gives a date, call this tool with { hm_id, hm_business_id, um_id, date, time } to show available slots.",
    parameters: {
      type: "object",
      properties: {
        um_id: {
          type: "integer",
          description: "User Management ID (Doctor ID)",
        },
        hm_id: {
          type: "integer",
          description: "Hospital Management ID",
        },
        appointment_date: {
          type: "string",
          description: "Date from user in YYYY-mm-dd format",
        },
        time: {
          type: "string",
          description: "Time from user in HH:mm format",
        },
        hm_business_id: {
          type: "string",
          description: "hospital business id",
        },
      },
      required: [
        "um_id",
        "hm_id",
        "appointment_date",
        "time",
        "hm_business_id",
      ],
    },
  },
  {
    type: "function",
    name: "get_patient_details",
    description:
      "Fetch patient profiles linked to a mobile number. Example: After user shares their number, call this with { mobile_number: '9876543210', hm_business_id: 10 }.",
    parameters: {
      type: "object",
      properties: {
        mobile_number: {
          type: "string",
          description: "Patient's mobile number",
        },
        hm_business_id: {
          type: "string",
          description: "hm_business_id",
        },
      },
      required: ["mobile_number", "hm_business_id"],
    },
  },
  {
    type: "function",
    name: "add_patient",
    description:
      "Add a new patient record if the user wants to register someone new. Example: When user says 'Add my son Arjun, male, born 2012', call with { name: 'Arjun', gender: 'Male', date_of_birth: '2012-05-10', hm_business_id: 10 }.",
    parameters: {
      type: "object",
      properties: {
        hm_id: {
          type: "integer",
          description: "Hospital Management ID",
        },
        um_id: { type: "integer", description: "User Management ID" },
        name: {
          type: "string",
          description: "Full name of the patient",
        },
        gender: {
          type: "string",
          description: "Gender (Male/Female/Other)",
        },
        date_of_birth: {
          type: "string",
          description: "Date of Birth in YYYY-MM-DD format",
        },
        phone: {
          type: "string",
          description: "Phone number of the patient",
        },
      },
      required: ["hm_id", "um_id", "name", "gender", "date_of_birth", "phone"],
    },
  },
  {
    type: "function",
    name: "book_appointment",
    description:
      "Book an appointment with a doctor for the selected patient. Example: Once user confirms date, doctor, and patient, call this with { hm_id, hm_business_id, um_id, doctor_name, patient_unique_id, pm_pid, date, appointment_start_time, appointment_end_time }.",
    parameters: {
      type: "object",
      properties: {
        um_id: {
          type: "string",
          description: "Doctor's User Management ID",
        },
        hm_id: {
          type: "string",
          description: "Hospital Management ID",
        },
        dp_id: { type: "integer", description: "Department ID" },
        patient_unique_id: {
          type: "string",
          description: "Unique ID for the patient",
        },
        pm_pid: {
          type: "string",
          description: "Patient ID in hospital",
        },
        appointment_date: {
          type: "string",
          description: "Appointment date in YYYY-MM-DD",
        },
        appointment_start_time: {
          type: "string",
          description: "Start time in HH:MM:SS",
        },
        appointment_end_time: {
          type: "string",
          description: "End time in HH:MM:SS",
        },
        appointment_duration: {
          type: "integer",
          description: "Duration in minutes (integer)",
        },
        patient_name: {
          type: "string",
          description: "Patient's name",
        },
        doctors_name: {
          type: "string",
          description: "Doctor's name",
        },
        patients_dob: {
          type: "string",
          description: "Patient's DOB",
        },
        patients_gender: {
          type: "string",
          description: "Patient's gender",
        },
        hm_business_id: {
          type: "string",
          description: "Hospital business id",
        },
      },
      required: [
        "um_id",
        "hm_id",
        "dp_id",
        "patient_unique_id",
        "pm_pid",
        "appointment_date",
        "appointment_start_time",
        "appointment_end_time",
        "appointment_duration",
        "patient_name",
        "doctors_name",
        "patients_dob",
        "patients_gender",
        "hm_business_id",
      ],
    },
  },
  {
    type: "function",
    name: "reschedule_appointment",
    description:
      "Reschedule an existing appointment. Example: When user says 'Move my appointment from 10 AM to 3 PM', call this with { pam_id, new_date, new_start_time, new_end_time }.",
    parameters: {
      type: "object",
      properties: {
        pam_id: {
          type: "string",
          description: "Patient Appointment Management ID",
        },
        um_id: {
          type: "string",
          description: "User ID (Receptionist User)",
        },
        hm_id: {
          type: "string",
          description: "Hospital Management ID",
        },
        patient_unique_id: {
          type: "string",
          description: "Unique Patient ID",
        },
        pm_pid: { type: "string", description: "Patient PID" },
        appointment_date: {
          type: "string",
          description: "Appointment Date (YYYY-MM-DD)",
        },
        appointment_start_time: {
          type: "string",
          description: "Appointment Start Time (HH:MM:SS)",
        },
        appointment_end_time: {
          type: "string",
          description: "Appointment End Time (HH:MM:SS)",
        },
        appointment_duration: {
          type: "integer",
          description: "Duration of Appointment in minutes",
        },
        patient_name: {
          type: "string",
          description: "Patient's name",
        },
        doctors_name: {
          type: "string",
          description: "Doctor's name",
        },
        patients_dob: {
          type: "string",
          description: "Patient's DOB",
        },
        patients_gender: {
          type: "string",
          description: "Patient's gender",
        },
        hm_business_id: {
          type: "string",
          description: "Hospital business id",
        },
      },
      required: [
        "pam_id",
        "um_id",
        "hm_id",
        "patient_unique_id",
        "pm_pid",
        "appointment_date",
        "appointment_start_time",
        "appointment_end_time",
        "appointment_duration",
        "patient_name",
        "doctors_name",
        "patients_dob",
        "patients_gender",
        "hm_business_id",
      ],
    },
  },
  {
    type: "function",
    name: "edit_patient",
    description: "Edit an existing patient's details in the hospital system",
    parameters: {
      type: "object",
      properties: {
        hm_id: {
          type: "integer",
          description: "Hospital Management ID",
        },
        um_id: { type: "integer", description: "User Management ID" },
        patient_unique_id: {
          type: "integer",
          description: "Unique ID of the patient",
        },
        name: {
          type: "string",
          description: "Full name of the patient",
        },
        gender: {
          type: "string",
          description: "Gender (Male/Female/Other)",
        },
        date_of_birth: {
          type: "string",
          description: "Date of Birth in YYYY-MM-DD format",
        },
        phone: {
          type: "integer",
          description: "Phone number of the patient",
        },
      },
      required: [
        "hm_id",
        "um_id",
        "patient_unique_id",
        "name",
        "gender",
        "date_of_birth",
        "phone",
      ],
    },
  },
];

// This function starts a session and returns the ephemeral key and session id
export async function startAzureOpenAISession(
  agentDetails: any,
  instructions: any,
  languageCode: string,
  hospitalType: string
) {
  try {
    const TOOLS_MAPPING: any = {
      APPOINTMENT_AGENT_VOICE_MODE_POLY_CLINIC: [
        ...TOOLS,
        ...MULTI_SPECIALITY_TOOL,
        ...MULTI_DOCTOR_TOOL,
      ],
      APPOINTMENT_AGENT_VOICE_MODE_SINGLE_SPECIALITY_SINGLE_DOCTOR: [...TOOLS],
      APPOINTMENT_AGENT_VOICE_MODE_SINGLE_SPECIALITY_MULTIPLE_DOCTOR: [
        ...TOOLS,
        ...MULTI_DOCTOR_TOOL,
      ],
    };

    const response = await fetch(SESSIONS_URL, {
      method: "POST",
      headers: {
        "api-key": API_KEY || "",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: DEPLOYMENT,
        input_audio_noise_reduction: {
          type: INPUT_AUDIO_NOISE_REDUCTION_TYPE.FAR_FIELD,
        },
        input_audio_transcription: {
          model: SYMPTOM_COLLECTOR_TRANSCRIPTION_MODEL,
          language: languageCode,
        },
        instructions: instructions,
        voice: agentDetails?.agent_voice,
        tool_choice: "auto",
        tools: TOOLS_MAPPING[hospitalType],
      }),
    });

    if (!response.ok) {
      throw new Error(`API request failed`);
    }

    const data = await response.json();
    const sessionId = data.id;
    const ephemeralKey = data.client_secret?.value;

    // Return both for further use
    return ephemeralKey;
  } catch (error) {
    console.error("Error fetching ephemeral key:", error);
    return null;
  }
}

export async function getSpecialities(body: any) {
  const { hm_id } = body || {};
  const headers = {
    "x-api-key": appointmentApiSecret,
  };
  const endpoint = `${appointmentApiUrl}/get-specilalities?hm_id=${hm_id}`;

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData ?? null;
  } catch (err) {
    console.error("Failed to handle get_specialities:", err);
    return [];
  }
}

export async function getDoctors(args: any) {
  const { hm_id, unique_speciality = false, filter_speciality = null } = args;
  const headers = {
    "x-api-key": appointmentApiSecret,
  };
  const endpoint = `${appointmentApiUrl}/get-doctors?hm_id=${hm_id}`;

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    let responseData = await response.json();

    if (filter_speciality) {
      return responseData.filter(
        (res: any) => res.speciality === filter_speciality
      );
    }
    if (unique_speciality) {
      return Array.from(
        new Set(responseData.map((res: any) => res.speciality))
      );
    }

    const cleanedData = responseData?.map(
      ({ hm_business_id, dp_id, ...rest }: any) => rest
    );

    return cleanedData ?? null;
  } catch (err) {
    console.error("Failed to handle get_doctors:", err);
    return [];
  }
}

export async function getDoctorsByName(args: any) {
  const { hm_id, um_name } = args;
  const headers = {
    "x-api-key": appointmentApiSecret,
  };
  const endpoint = `${appointmentApiUrl}/get-doctors-by-name?hm_id=${hm_id}&um_name=${encodeURIComponent(
    um_name
  )}`;

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData ?? null;
  } catch (err) {
    console.error("Failed to handle get_doctors_by_name:", err);
    return [];
  }
}

export async function getAppointmentSlots(body: any) {
  const { um_id, hm_id, hm_business_id, appointment_date } = body;
  const headers = {
    "x-api-key": appointmentApiSecret,
  };
  const endpoint = `${appointmentApiUrl}/get-appointment-slots?um_id=${um_id}&hm_id=${hm_id}&hm_business_id=${hm_business_id}&date=${appointment_date}`;
  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData ?? null;
  } catch (err) {
    console.error("Failed to handle get_appointment_slots:", err);
    return [];
  }
}

export async function getAppointmentSlotsForVoice(body: any) {
  const { um_id, hm_id, hm_business_id, appointment_date, time } = body;
  const headers = {
    "x-api-key": appointmentApiSecret,
  };
  const endpoint = `${appointmentApiUrl}/get-appointment-slots-voice?um_id=${um_id}&hm_id=${hm_id}&hm_business_id=${hm_business_id}&date=${appointment_date}&time=${
    time && time !== "00:00" ? time : ""
  }`;
  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData ?? null;
  } catch (err) {
    console.error("Failed to handle get_appointment_slots:", err);
    return [];
  }
}

export async function getPatientDetails(args: any) {
  const { mobile_number, hm_business_id } = args;
  const headers = {
    "x-api-key": appointmentApiSecret,
  };
  const endpoint = `${appointmentApiUrl}/get-patient-details?mobile_number=${mobile_number}&hm_business_id=${hm_business_id}`;
  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData ?? null;
  } catch (err) {
    console.error("Failed to handle get_patient_details:", err);
    return [];
  }
}

export async function addNewPatient(args: any) {
  const {
    hm_id,
    um_id,
    salutation,
    name,
    gender,
    date_of_birth,
    mobile_number,
    phone,
  } = args;

  const headers = {
    "x-api-key": appointmentApiSecret,
    "Content-Type": "application/json",
  };

  const endpoint = `${appointmentApiUrl}/add-patient`;
  const payload = {
    hm_id,
    um_id,
    salutation,
    name,
    gender,
    date_of_birth,
    phone: mobile_number || phone,
  };

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData ?? {};
  } catch (err) {
    console.error("Failed to handle add_new_patient:", err);
    return {};
  }
}

export async function editPatient(args: any) {
  const {
    patient_unique_id,
    hm_id,
    um_id,
    salutation,
    name,
    gender,
    date_of_birth,
    mobile_number,
    phone,
  } = args;

  const headers = {
    "x-api-key": appointmentApiSecret,
    "Content-Type": "application/json",
  };

  const endpoint = `${appointmentApiUrl}/edit-patient`;
  const payload = {
    patient_unique_id,
    hm_id,
    um_id,
    salutation,
    name,
    gender,
    date_of_birth,
    phone: mobile_number || phone,
  };

  let responseData: any = {};

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();
    return { ...responseData, status: response.status };
  } catch (err) {
    console.error("Failed to handle edit_patient:", err);
    return responseData;
  }
}

export async function bookAppointment(body: any) {
  const {
    um_id,
    hm_id,
    patient_unique_id,
    pm_pid,
    appointment_date,
    appointment_start_time,
    appointment_end_time,
    appointment_duration,
    hospital_business_id,
    hm_business_id,
  } = body;
  const headers = {
    "x-api-key": appointmentApiSecret,
    "content-type": "application/json",
  };
  const endpoint = `${appointmentApiUrl}/book-appointment`;
  const payload = {
    um_id: um_id,
    hm_id: hm_id,
    patient_unique_id,
    pm_pid,
    appointment_date: appointment_date,
    appointment_start_time,
    appointment_end_time,
    appointment_duration,
    hm_business_id: hospital_business_id || hm_business_id,
  };
  let responseData: any = {};
  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();
    return { ...responseData, status: response.status };
  } catch (err) {
    console.error("Failed to handle book_appointment:", err);
    return responseData;
  }
}

export async function cancelAppointment(body: any) {
  const {
    pam_id,
    um_id,
    hm_id,
    patient_unique_id,
    pm_pid,
    appointment_date,
    appointment_start_time,
    appointment_end_time,
    appointment_duration,
    dp_id,
  } = body;

  const headers = {
    "x-api-key": appointmentApiSecret,
    "Content-Type": "application/json",
  };

  const endpoint = `${appointmentApiUrl}/cancel-appointment`;
  const payload = {
    pam_id,
    um_id,
    hm_id,
    patient_unique_id,
    pm_pid,
    appointment_date: appointment_date
      ? dayjs(appointment_date).format("YYYY-MM-DD")
      : undefined,
    appointment_start_time,
    appointment_end_time,
    appointment_duration,
    dp_id,
  };

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseData = await response?.status;
    return responseData ?? {};
  } catch (err) {
    console.error("Failed to handle cancel_appointment:", err);
    return {};
  }
}

export async function rescheduleAppointment(body: any) {
  const {
    pam_id,
    um_id,
    hm_id,
    patient_unique_id,
    pm_pid,
    appointment_date,
    appointment_start_time,
    appointment_end_time,
    appointment_duration,
    dp_id,
  } = body;

  const headers = {
    "x-api-key": appointmentApiSecret,
    "Content-Type": "application/json",
  };

  const endpoint = `${appointmentApiUrl}/reschedule-appointment`;
  const payload = {
    pam_id,
    um_id,
    hm_id,
    patient_unique_id,
    pm_pid,
    appointment_date: appointment_date
      ? dayjs(appointment_date).format("YYYY-MM-DD")
      : undefined,
    appointment_start_time,
    appointment_end_time,
    appointment_duration,
    dp_id,
  };

  let responseData: any = {};
  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    responseData = await response.json();
    return { ...responseData, status: response.status };
  } catch (err) {
    console.error("Failed to handle reschedule_appointment:", err);
    return responseData;
  }
}

export async function fetchAvatars() {
  const headers = {
    "x-api-key": appointmentApiSecret,
  };

  try {
    const response = await fetch(`${appointmentApiUrl}/avatars`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData ?? null;
  } catch (err) {
    console.error("Failed to handle get_avatars:", err);
    return [];
  }
}

export async function getInstructions(
  selectedLanguage: string,
  chat_history: any,
  state: any,
  hospitalType: any,
  doctorsListRef: any,
  selectedDoctor: any
) {
  const {
    agentDetails,
    um_id,
    patient_unique_id,
    pm_pid,
    appointment_date,
    appointment_start_time,
    appointment_end_time,
    appointment_duration,
    pam_id,
    pm_gender,
    pm_first_name,
    pm_last_name,
    pm_dob,
    um_name,
  } = state;

  const APPOINTMENT_BOT_PROMPT_MAPPING: any = {
    APPOINTMENT_AGENT_VOICE_MODE_POLY_CLINIC: "POLY CLINIC HOSPITAL",
    APPOINTMENT_AGENT_VOICE_MODE_SINGLE_SPECIALITY_SINGLE_DOCTOR:
      "SINGLE SPECIALITY SINGLE DOCTOR HOSPITAL",
    APPOINTMENT_AGENT_VOICE_MODE_SINGLE_SPECIALITY_MULTIPLE_DOCTOR:
      "SINGLE SPECIALITY MULTIPLE DOCTORS HOSPITAL",
  };

  const hospitalTypeForBot = APPOINTMENT_BOT_PROMPT_MAPPING[hospitalType];

  try {
    const {
      hm_id,
      hm_business_id,
      patient_contact_no: mobile_number,
      hospital_name,
      agent_gender,
      agent_name,
      agent_voice: voice,
      agent_accent,
    } = agentDetails || {};

    const doctorsList = await getDoctors({ hm_id });
    doctorsListRef.current = doctorsList;

    const headers = {
      "x-api-key": appointmentApiSecret,
    };

    const base = `${CONVERSATION_ID_URL}/api/v1/prompts/${
      hospitalType ===
      "APPOINTMENT_AGENT_VOICE_MODE_SINGLE_SPECIALITY_SINGLE_DOCTOR"
        ? "APPOINTMENT_AGENT_VOICE_MODE_SINGLE_SPECIALITY_SINGLE_DOCTOR_2"
        : hospitalType ===
          "APPOINTMENT_AGENT_VOICE_MODE_SINGLE_SPECIALITY_MULTIPLE_DOCTOR"
        ? "APPOINTMENT_AGENT_VOICE_MODE_SINGLE_SPECIALITY_MULTIPLE_DOCTOR_2"
        : hospitalType === "APPOINTMENT_AGENT_VOICE_MODE_POLY_CLINIC"
        ? "APPOINTMENT_AGENT_VOICE_MODE_POLY_CLINIC_2"
        : hospitalType
    }`;

    const query =
      `today_date=${dayjs().format("DD-MM-YYYY")}` +
      `hm_id=${hm_id ?? ""}` +
      `&hm_business_id=${hm_business_id ?? ""}` +
      `&mobile_number=${mobile_number ?? ""}` +
      `&hospital_name=${hospital_name ?? ""}` +
      `&agent_gender=${agent_gender ?? ""}` +
      `&agent_name=${agent_name ?? ""}` +
      `&language=${selectedLanguage}` +
      `&voice=${voice ?? ""}` +
      `&agent_accent=${agent_accent ?? ""}` +
      // `&chat_history=${JSON.stringify(chat_history ?? [])}` +
      `&um_id=${um_id || selectedDoctor?.um_id || ""}` +
      `&patient_unique_id=${patient_unique_id ?? ""}` +
      `&pm_pid=${pm_pid ?? ""}` +
      `&pm_gender=${pm_gender ?? ""}` +
      `&pm_first_name=${pm_first_name ?? ""}` +
      `&pm_last_name=${pm_last_name ?? ""}` +
      `&pm_dob=${pm_dob ?? ""}` +
      `&appointment_date=${appointment_date ?? ""}` +
      `&appointment_start_time=${appointment_start_time ?? ""}` +
      `&appointment_end_time=${appointment_end_time ?? ""}` +
      `&appointment_duration=${String(appointment_duration ?? "")}` +
      `&pam_id=${pam_id ?? ""}` +
      `&prefix=&open={&close=}` +
      `&clinic_type=${hospitalTypeForBot}` +
      `&doctors_name=${um_name ?? ""}` +
      // `&doctors_list=${JSON.stringify(doctorsList ?? [])}` +
      `${
        hospitalType ===
        "APPOINTMENT_AGENT_VOICE_MODE_SINGLE_SPECIALITY_SINGLE_DOCTOR"
          ? `&doctor_name=${doctorsList[0]?.um_name ?? ""}`
          : ""
      }` + //only for single doctor single speciality
      `&doctor_list=${JSON.stringify(doctorsList ?? [])}`;

    const endpoint = `${base}?${query}`;

    const response = await fetch(endpoint, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData?.prompt ?? null;
  } catch (err) {
    console.error("Failed to fetch voice mode prompt:", err);
    return null;
  }
}

export async function fetchClinicLogo(logoName: string) {
  const headers = {
    "x-api-key": appointmentApiSecret,
  };

  try {
    const response = await fetch(
      `${appointmentApiUrl}/agent-logo?logo=${logoName}`,
      {
        method: "GET",
        headers,
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData?.link ?? null;
  } catch (err) {
    console.error("Failed to handle get_avatars:", err);
    return [];
  }
}

export async function getAppointmentsList(
  hm_id: string,
  mobile_number: string
) {
  const headers = {
    "x-api-key": appointmentApiSecret,
  };

  const endpoint = `${appointmentApiUrl}/list-appointments?hm_id=${hm_id}&mobile_number=${mobile_number}`;

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData?.reverse() ?? [];
  } catch (err) {
    console.error("Failed to fetch appointments list:", err);
    return [];
  }
}

export const saveConversationData = async (
  userTranscription: any,
  agentTranscription: any,
  conversationId: any,
  conversationChunks: any,
  transcribedParagraph: any,
  selectedLanguage: string,
  um_id: string,
  patient_unique_id: string,
  hm_id: string,
  pam_id: string,
  hm_business_id: any,
  pm_pid: any,
  type: string
) => {
  try {
    const uuid = generateUUID();
    const endpoint = `${CONVERSATION_ID_URL}/api/v1/receptionist-agent/save-conversation`;
    const formdata = new FormData();

    const audioBlob = new Blob(conversationChunks, {
      type: "audio/webm",
    });

    const audioFile = audioBlob;

    const audioFilename = [`${uuid}.webm`];

    if (conversationChunks.length > 0) {
      formdata.append("audio_file", audioFile, `${uuid}.webm`);
      formdata.append("audio_filename", JSON.stringify(audioFilename));
    } else {
      const data: any = null;
      formdata.append("audio_file", data);
      formdata.append("audio_filename", data);
    }

    formdata.append("user_transcriptions", JSON.stringify(userTranscription));
    formdata.append("agent_transcriptions", JSON.stringify(agentTranscription));
    formdata.append("summary", JSON.stringify(transcribedParagraph));
    formdata.append("um_id", um_id);
    formdata.append("patient_unique_id", patient_unique_id);
    formdata.append("hm_id", hm_id);
    formdata.append("pam_id", pam_id);
    formdata.append("language", selectedLanguage);
    formdata.append("hm_business_id", hm_business_id);
    formdata.append("pm_pid", pm_pid);
    formdata.append("type", type);

    if (conversationId) {
      formdata.append("_id", conversationId);
    }

    const headers = {
      "x-api-key": appointmentApiSecret,
    };

    const response = await fetch(endpoint, {
      method: "POST",
      headers,
      body: formdata,
    });

    const data = await response.json();
    return data?.result;
  } catch (error) {
    console.error("Error saving conversation:", error);
    return null;
  }
};

export const fetchSymptomsCollectorLink = async (body: any) => {
  const {
    um_id,
    patient_unique_id,
    patient_contact_no,
    pam_id,
    clinic_id,
    hm_id,
    hospital_name,
    hospital_business_id,
    hm_business_id,
    patient_name,
    doctor_name,
  } = body;

  const headers = {
    "x-api-key": appointmentApiSecret,
    "Content-Type": "application/json",
  };

  const endpoint = `${appointmentApiUrl}/get-symptoms-collector-link`;
  const payload = {
    um_id,
    patient_unique_id,
    patient_contact_no,
    pam_id,
    clinic_id,
    hm_id,
    hospital_name,
    hospital_business_id: hospital_business_id || hm_business_id,
    patient_name,
    doctor_name,
  };

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData?.link ?? null;
  } catch (err) {
    console.error("Failed to get symptoms collector link:", err);
    return null;
  }
};

export const getReceptionistAgents = async (clinicId: string) => {
  const headers = {
    "x-api-key": appointmentApiSecret,
  };

  const endpoint = `${appointmentApiUrl}/receptionist-agents?clinicId=${clinicId}`;

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData?.token ?? null;
  } catch (err) {
    console.error("Failed to get receptionist agents:", err);
    return null;
  }
};

export const getConversation = async (params: {
  pam_id: string;
  um_id: string;
  hm_id: string;
  patient_unique_id: string;
}) => {
  const headers = {
    "x-api-key": appointmentApiSecret,
  };

  const { pam_id, um_id, hm_id, patient_unique_id } = params;
  const endpoint = `${appointmentApiUrl}/get-conversation?pam_id=${pam_id}&um_id=${um_id}&hm_id=${hm_id}&patient_unique_id=${patient_unique_id}`;

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData ?? null;
  } catch (err) {
    console.error("Failed to get conversation:", err);
    return null;
  }
};

// Function to convert 24-hour time to 12-hour format with AM/PM using dayjs
export const formatTime12Hour = (time24: string) => {
  if (!time24) return "";
  return dayjs(time24, "HH:mm:ss").format("hh:mm A");
};

export async function appointmentSlotsValidator(args: {
  um_id: number;
  hm_id: number;
  appointment_date: string;
  hm_business_id: string;
}) {
  const { um_id, hm_id, appointment_date, hm_business_id } = args;
  const headers = {
    "x-api-key": appointmentApiSecret,
  };

  const endpoint = `${appointmentApiUrl}/get-appointment-slots-voice?um_id=${um_id}&hm_id=${hm_id}&hm_business_id=${hm_business_id}&appointment_date=${appointment_date}`;

  try {
    console.log(
      `Validating appointment slots for um_id=${um_id}, hm_id=${hm_id}, hm_business_id=${hm_business_id}, appointment_date=${appointment_date}`
    );

    const response = await fetch(endpoint, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`HTTP error: ${response.status} - ${errorText}`);
      return `HTTP error occurred: ${response.status} - ${errorText}`;
    }

    return await response.json();
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("fetch")) {
      console.error(
        "Connection error occurred while validating appointment slots:",
        error
      );
      return "Connection error occurred while validating appointment slots";
    }

    console.error(
      "Unexpected error occurred while validating appointment slots:",
      error
    );
    return {
      error: `Unexpected error occurred: ${
        error instanceof Error ? error.message : String(error)
      }`,
      available_slots: [],
      is_available: false,
    };
  }
}